import { defineStore } from 'pinia'
import localStorageService from '@/core/localstorage.service'
import authService from '@/services/auth.service'
import router from '../router'
import { useCompanyStore } from './company'
import { useGeneralStore } from './general'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorageService.getItem('eeToken') || '',
    /* Held in memory only, for the login -> 2fa hop. Vue Router 4 drops params
       that aren't part of the route path, so the token can't ride along on the
       push the way it did under Vue Router 3. */
    pendingToken: ''
  }),

  getters: {
    authToken: state => state.token
  },

  actions: {
    setToken (value) {
      this.token = value
      localStorageService.setItem('eeToken', value)
    },

    setPendingToken (value) {
      this.pendingToken = value
    },

    removeToken () {
      this.token = ''
      this.pendingToken = ''
      localStorageService.removeItem('eeToken')
      localStorageService.removeItem('userFromRedirect')
      localStorageService.removeItem('managerId')
      localStorageService.removeItem('adminToken')
      localStorageService.removeItem('investorId')
      localStorageService.removeItem('currentInvestor')
    },

    async getToken (user) {
      try {
        const { data } = await authService.getToken(user)
        const generalStore = useGeneralStore()
        generalStore.setUserData({ verify_login: data.verify_login })
        return data
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async login ({ accessToken }) {
      this.setToken(accessToken)
      const generalStore = useGeneralStore()
      return generalStore.getGlobalData()
    },

    async handle2FA ({ accessToken, secret }) {
      try {
        await authService.verify2FA(secret, accessToken)
        this.setToken(accessToken)
        this.pendingToken = ''
        const generalStore = useGeneralStore()
        return generalStore.getGlobalData()
      } catch (err) {
        return Promise.reject(err)
      }
    },

    loginFromRedirect ({ token, managerId, investorId, referrer, adminToken }) {
      this.setToken(token)
      localStorageService.setItem('userFromRedirect', referrer || process.env.VUE_APP_API)

      if (managerId) localStorageService.setItem('managerId', managerId)
      if (adminToken) localStorageService.setItem('adminToken', adminToken)
      if (investorId) localStorageService.setItem('investorId', investorId)
    },

    async logoutRedirect (next) {
      const userFromRedirect = localStorageService.getItem('userFromRedirect')
      this.removeToken()

      if (userFromRedirect) {
        window.location.href = `${userFromRedirect}/logout`
        return
      }
      const companyStore = useCompanyStore()
      const generalStore = useGeneralStore()
      const baseRouteConfig = companyStore.baseRouteConfig
      const hasQuery = baseRouteConfig && baseRouteConfig.query && Object.keys(baseRouteConfig.query).length > 0

      if (hasQuery) {
        next({ ...baseRouteConfig, name: 'login', params: { language: generalStore.currentLocale } })
        return
      }

      next()
    },

    idleRedirect () {
      this.removeToken()
      const companyStore = useCompanyStore()
      const generalStore = useGeneralStore()
      router.push({
        ...companyStore.baseRouteConfig,
        name: 'login',
        params: { language: generalStore.currentLocale }
      })
    },

    // redirect action for company that do not want to show dashboard
    blockDashboardRedirect () {
      const companyStore = useCompanyStore()
      router.push({ ...companyStore.baseRouteConfig, name: 'statements' })
    }
  }
})
